import { useMemo, useState, useEffect } from 'react';
import { useWorkOrderCharges } from '@api/enterprise/workorder';
import { useRepairPartsSearch } from '@api/enterprise/repairParts';
import { useOfflineDownloadService } from '@services/offlineDownloadService';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { subscribe } from '@services/offlineSyncService';
import type {
  RepairPartViewModel,
  WorkOrderChargeViewModel
} from '@api/enterprise/openapi';

export const useWorkOrderPartsData = (jobNumber: string) => {
  const isOnline = useOnlineStatus();
  const { getCachedCharges } = useOfflineDownloadService();

  const [offlineCharges, setOfflineCharges] = useState<
    WorkOrderChargeViewModel[]
  >([]);
  const [offlineLoading, setOfflineLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const {
    data: allCharges,
    isLoading: isLoadingCharges,
    error: chargesError
  } = useWorkOrderCharges(jobNumber, isOnline);

  useEffect(() => {
    if (!isOnline) {
      return subscribe(() => setRefreshKey(k => k + 1));
    }
  }, [isOnline]);

  useEffect(() => {
    const fetchOfflineCharges = async () => {
      if (!isOnline && jobNumber) {
        setOfflineLoading(true);
        try {
          const cached = await getCachedCharges(jobNumber);
          setOfflineCharges(cached);
        } catch {
          setOfflineCharges([]);
        } finally {
          setOfflineLoading(false);
        }
      }
    };

    fetchOfflineCharges();
  }, [isOnline, jobNumber, getCachedCharges, refreshKey]);

  const chargesData = isOnline ? allCharges : offlineCharges;

  const workOrderParts = useMemo(() => {
    if (!chargesData) return [];
    return chargesData.filter(charge => charge.chargeType === 'REPAIR PART');
  }, [chargesData]);

  const partIdentifiers = useMemo(() => {
    if (!workOrderParts?.length) return [];
    return workOrderParts
      .map(part => ({
        company: (part as any).partCompanyName,
        partNumber: (part as any).partNumber
      }))
      .filter(p => p.company && p.partNumber);
  }, [workOrderParts]);

  const filter = useMemo(() => {
    if (!partIdentifiers.length) return undefined;

    const filters = partIdentifiers.map(
      ({ company, partNumber }) =>
        `(companyName eq '${company}' and partNumber eq '${partNumber}')`
    );

    return filters.join(' or ');
  }, [partIdentifiers]);

  const { data: repairPartsData, isLoading: isLoadingRepairParts } =
    useRepairPartsSearch(
      {
        filter,
        select: [
          'partNumber',
          'partName',
          'description',
          'companyName',
          'cost'
        ],
        top: 100
      },
      !!filter && isOnline
    );

  const enrichedParts = useMemo(() => {
    if (!workOrderParts) return [];

    const repairPartsMap = new Map<string, RepairPartViewModel>();
    const parts = Array.isArray(repairPartsData)
      ? repairPartsData
      : repairPartsData?.value || [];
    parts.forEach(part => {
      if (part.companyName && part.partNumber) {
        const key = `${part.companyName}-${part.partNumber}`;
        repairPartsMap.set(key, part);
      }
    });

    return workOrderParts.map((workOrderPart, index) => {
      const partCompanyName = (workOrderPart as any).partCompanyName;
      const partNumber = (workOrderPart as any).partNumber;
      const key = `${partCompanyName}-${partNumber}`;
      const repairPart = repairPartsMap.get(key);

      return {
        ...workOrderPart,
        id: `${jobNumber}-part-${index}`,
        partNumber,
        partName: repairPart?.partName || null,
        companyName: partCompanyName,
        description: repairPart?.description || '-',
        cost: repairPart?.cost
      };
    });
  }, [workOrderParts, repairPartsData, jobNumber]);

  return {
    parts: enrichedParts,
    isLoading: isOnline
      ? isLoadingCharges || isLoadingRepairParts
      : offlineLoading,
    error: isOnline ? chargesError : null
  };
};
